import * as Yup from 'yup';
import { TFunction } from 'i18next';

const initialValues: any = {
  skills: [],
  degree: null,
  industries: [],
  is_public: false,
  job_description: '',
  job_title: '',
  locations: [],
  required_experience: null,
  musts: [],
  culture_profile: null,
  is_remote: false,
  if_office_work: false,
  status: 'published',
  contract_type: null,
  compensation: null
};

const toNumber = (value: unknown) => {
  if (value === '' || value == null) return undefined;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const toArray = (value: unknown): unknown[] => {
  if (value == null || value === '') return [];
  if (Array.isArray(value)) return value;
  return [value];
};

const toIdArray = (value: unknown): Array<number | string> => {
  return toArray(value)
    .map((item) => {
      if (typeof item === 'object' && item !== null && 'id' in item) {
        return (item as { id: number | string }).id;
      }
      return item as number | string;
    })
    .filter(
      (item): item is number | string =>
        item !== undefined && item !== null && item !== ''
    );
};

const stripHtml = (value?: string | null) =>
  (value || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const validation = (t: TFunction) => {
  return Yup.object().shape({
    skills: Yup.array()
      .transform((value) => toArray(value))
      .min(1, t('Skills must have at least one item'))
      .required(t('Skills are required')),
    degree: Yup.number()
      .transform((value) => toNumber(value))
      .moreThan(0, t('Degree is required'))
      .required(t('Degree is required')),
    status: Yup.string().trim().required(t('Status is required')),
    contract_type: Yup.number()
      .transform((value) => toNumber(value))
      .required(t('Contract is required')),
    industries: Yup.array()
      .transform((value) => toIdArray(value))
      .min(1, t('Industries must have at least one item'))
      .required(t('Industries are required')),
    locations: Yup.array()
      .transform((value) =>
        toArray(value).filter(
          (item) =>
            item &&
            typeof item === 'object' &&
            'id' in item &&
            (item as { id: number }).id !== -1
        )
      )
      .min(1, t('At least one location is required'))
      .required(t('Locations are required')),
    is_public: Yup.bool().default(false).required(),
    is_remote: Yup.bool().default(false).required(),
    if_office_work: Yup.bool().default(false).required(),
    job_description: Yup.string()
      .required(t('Job description is required'))
      .test(
        'job-description-min',
        t('Job description must be at least 3 characters'),
        (value) => stripHtml(value).length >= 3
      ),
    job_title: Yup.string()
      .trim()
      .min(3, t('Job title must be at least 3 characters'))
      .max(300, t('Job title must be less than 300 characters'))
      .required(t('Job title is required')),
    compensation: Yup.number()
      .transform((value) => toNumber(value))
      .required(t('Compensation is required')),
    required_experience: Yup.number()
      .transform((value) => toNumber(value))
      .required(t('Required experience is required')),
    musts: Yup.array()
      .of(Yup.string())
      .transform((value) => toArray(value))
      .default([]),
    culture_profile: Yup.mixed()
      .required(t('Culture profile is required'))
      .test(
        'culture-profile-valid',
        t('Culture profile is required'),
        (value) => {
          if (!value || typeof value !== 'object') return false;
          const id = (value as { id?: number }).id;
          return id != null && id > 0;
        }
      )
  });
};

export { initialValues, validation };
